import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = '/api';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [language, setLanguage] = useState(localStorage.getItem('language') || 'fr');
  const [distanceUnit, setDistanceUnit] = useState(localStorage.getItem('distanceUnit') || 'km');

  // Configure axios defaults
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Check token validity on mount
  useEffect(() => {
    const checkAuth = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API_URL}/auth/me`);
        setUser(response.data);
        if (response.data.preferred_language) {
          setLanguage(response.data.preferred_language);
          localStorage.setItem('language', response.data.preferred_language);
        }
        if (response.data.preferred_distance_unit) {
          setDistanceUnit(response.data.preferred_distance_unit);
          localStorage.setItem('distanceUnit', response.data.preferred_distance_unit);
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    
    if (userData.preferred_language) {
      setLanguage(userData.preferred_language);
      localStorage.setItem('language', userData.preferred_language);
    }
    if (userData.preferred_distance_unit) {
      setDistanceUnit(userData.preferred_distance_unit);
      localStorage.setItem('distanceUnit', userData.preferred_distance_unit);
    }
    
    return userData;
  };

  const register = async (email, password, username, invitationToken = null) => {
    const response = await axios.post(`${API_URL}/auth/register`, {
      email,
      password,
      username,
      preferred_language: language,
      preferred_distance_unit: distanceUnit,
      invitation_token: invitationToken || undefined,
    });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  const persistPreferences = (preferences) => {
    if (!user) return;
    axios.patch(`${API_URL}/auth/preferences`, preferences)
      .then((response) => setUser(response.data))
      .catch((error) => console.error('Preference update failed:', error));
  };

  const switchLanguage = (lang) => {
    setLanguage(lang);
    localStorage.setItem('language', lang);
    persistPreferences({ preferred_language: lang });
  };

  const switchDistanceUnit = (unit) => {
    if (!['km', 'mi'].includes(unit)) return;
    setDistanceUnit(unit);
    localStorage.setItem('distanceUnit', unit);
    persistPreferences({ preferred_distance_unit: unit });
  };

  const value = {
    user,
    token,
    loading,
    language,
    distanceUnit,
    isAuthenticated: !!user,
    isAdmin: !!user?.is_admin,
    login,
    register,
    logout,
    switchLanguage,
    switchDistanceUnit
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;

